import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { MessageSquare, Plus, Archive, ArchiveRestore, Users, Ban, Briefcase } from "lucide-react";

export default function ConversationsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: "direct", handles: "", title: "" });
  const [formError, setFormError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/conversations");
      setItems(res.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setFormError("");
    setCreating(true);
    try {
      const res = await api.post("/conversations", {
        type: form.type,
        handles: form.handles.split(",").map((h) => h.trim()).filter(Boolean),
        title: form.title,
      });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleArchive = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await api.post(`/conversations/${conversationId}/archive`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="conversations-list" className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-brand-navy">Messagerie</h1>
        <button
          data-testid="new-conversation-button"
          onClick={() => setShowNew(!showNew)}
          className="inline-flex items-center gap-1.5 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors duration-200"
        >
          <Plus className="w-4 h-4" />
          Nouvelle conversation
        </button>
      </div>

      {showNew && (
        <form data-testid="new-conversation-form" onSubmit={create} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          {formError && (
            <div data-testid="new-conversation-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{formError}</div>
          )}
          <div className="flex gap-2">
            {["direct", "group"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`conversation-type-${t}`}
                onClick={() => setForm({ ...form, type: t })}
                className={`text-xs font-medium rounded-lg px-4 py-2 border transition-colors ${form.type === t ? "bg-brand-navy text-white border-brand-navy" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              >
                {t === "direct" ? "Directe" : "Groupe"}
              </button>
            ))}
          </div>
          <input
            data-testid="new-conversation-handles"
            value={form.handles}
            onChange={(e) => setForm({ ...form, handles: e.target.value })}
            placeholder={form.type === "direct" ? "@identifiant du destinataire" : "@identifiants séparés par des virgules"}
            className={inputCls}
            required
          />
          {form.type === "group" && (
            <input
              data-testid="new-conversation-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Titre du groupe"
              className={inputCls}
              required
            />
          )}
          <button
            data-testid="new-conversation-submit"
            type="submit"
            disabled={creating}
            className="bg-brand-navy text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-blue transition-colors duration-200 disabled:opacity-50"
          >
            {creating ? "Création…" : "Créer la conversation"}
          </button>
        </form>
      )}

      {error && (
        <div data-testid="conversations-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="space-y-2">
        {items.map((c) => (
          <div
            key={c.conversation_id}
            data-testid={`conversation-${c.conversation_id}`}
            onClick={() => navigate(`/messages/${c.conversation_id}`)}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-brand-turquoise hover:shadow-sm transition-all duration-200"
          >
            <div className="min-w-0 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-brand-sky flex items-center justify-center shrink-0">
                {c.type === "group" ? <Users className="w-4 h-4 text-brand-navy" /> : c.type === "application" ? <Briefcase className="w-4 h-4 text-brand-navy" /> : <MessageSquare className="w-4 h-4 text-brand-navy" />}
              </span>
              <div className="min-w-0">
                <p className="font-medium text-brand-navy text-sm truncate flex items-center gap-2">
                  {c.display_name}
                  {c.archived && <span className="text-[10px] font-medium bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Archivée</span>}
                  {c.blocked && <span className="text-[10px] font-medium bg-red-50 text-red-600 rounded-full px-2 py-0.5">Bloquée</span>}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {c.last_message ? `${c.last_message.sender_name} : ${c.last_message.text || "(pièce jointe)"}` : "Aucun message"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {c.unread_count > 0 && (
                <span data-testid={`unread-${c.conversation_id}`} className="text-xs font-bold bg-brand-turquoise text-white rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">
                  {c.unread_count}
                </span>
              )}
              <button
                data-testid={`archive-${c.conversation_id}`}
                onClick={(e) => toggleArchive(e, c.conversation_id)}
                title={c.archived ? "Désarchiver" : "Archiver"}
                className="text-slate-400 hover:text-brand-navy transition-colors"
              >
                {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p data-testid="conversations-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-10 text-center">
            Aucune conversation. Démarrez-en une avec un @identifiant NEXA.
          </p>
        )}
      </div>
    </div>
  );
}
